// Emphasis follows the mobile markdown parser, so a message reads the same on every client.
export const EMPHASIS_RULES = [
    { type: 'bold', pattern: /\*\*(.+?)\*\*/ },
    { type: 'bold', pattern: /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/ },
    { type: 'italic', pattern: /_(.+?)_/ },
    { type: 'strike', pattern: /~~(.+?)~~/ },
    { type: 'strike', pattern: /(?<!~)~(?!~)(.+?)(?<!~)~(?!~)/ },
];
export const CODE_SPAN = /`([^`\n]+)`/g;
// Groups: url, www link, email, phone, mention placeholder, @all.
export const ATOMS = new RegExp(
    [
        '(https?:\\/\\/[^\\s<]+)',
        '((?<![\\w/])www\\.[^\\s<]+)',
        '([a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,})',
        '(\\+\\d[\\d\\s.-]{7,16}\\d|(?:^|\\s)0\\d[\\d\\s.-]{7,12}\\d)',
        '(@\\[\\?\\])',
        '((?<![\\w@])@all\\b)',
    ].join('|'),
    'gi',
);
const HTTP_URL = /https?:\/\/[^\s<]+/gi;
const TRAILING_PUNCTUATION = /[.,!?;:)\]'"]+$/;
export const FENCE_LINE = /^\s*```\s*$/;
const BULLET_LINE = /^\s*[-*]\s+(.*)$/;
const NUMBER_LINE = /^\s*(\d+)\.\s(.*)$/;
const QUOTE_LINE = /^\s*>\s(.*)$/;
// Each atom is parked on one private-use character while emphasis is matched around it.
const ATOM_BASE = 0xe000;
const ATOM_MARK = /[-]/g;

/**
 * Splits message text into blocks of inline runs, for the bubble to render as React nodes.
 *
 * Blocks: `{ type: 'line', runs }`, `{ type: 'gap' }`, `{ type: 'quote', lines }`,
 * `{ type: 'list', ordered, items: [{ value, runs }] }`, `{ type: 'pre', text }`.
 * Runs: `text`, `code`, `bold` / `italic` / `strike` (with `runs`), `link` (`href`, `text`),
 * `mention` (`userId`), `mentionAll`.
 *
 * @param {string} text Message text; mentions are `@[?]` placeholders.
 * @param {string[]} mentionedUsersIds Mentioned users, in placeholder order.
 * @returns {object[]} Blocks in display order.
 */
export const parseRichText = (text, mentionedUsersIds = []) => {
    const mentionIds = [...(mentionedUsersIds || [])];
    const blocks = [];
    let fence = null;

    cleanLegacyMarkup(text)
        .split('\n')
        .forEach(line => {
            if (FENCE_LINE.test(line)) {
                if (fence) blocks.push({ type: 'pre', text: fence.join('\n') });
                fence = fence ? null : [];
                return;
            }
            if (fence) {
                fence.push(line);
                return;
            }
            appendLine(blocks, line, mentionIds);
        });
    // An unclosed fence is not a block: its marker shows as typed.
    if (fence) ['```', ...fence].forEach(line => appendLine(blocks, line, mentionIds));
    return blocks;
};

/**
 * Lists the http(s) links of a message in text order. Link preview indexes count from 1 in this list.
 *
 * @param {string} text Message text.
 * @returns {string[]} Links, trailing punctuation removed.
 */
export const extractUrls = (text = '') =>
    (cleanLegacyMarkup(text).match(HTTP_URL) || []).map(url => url.replace(TRAILING_PUNCTUATION, ''));

const appendLine = (blocks, line, mentionIds) => {
    const last = blocks[blocks.length - 1];
    const bullet = line.match(BULLET_LINE);
    const number = !bullet && line.match(NUMBER_LINE);
    const quote = !bullet && !number && line.match(QUOTE_LINE);

    if (bullet || number) {
        const ordered = Boolean(number);
        const item = {
            value: number ? Number(number[1]) : undefined,
            runs: parseInline(number ? number[2] : bullet[1], mentionIds),
        };
        if (last?.type === 'list' && last.ordered === ordered) last.items.push(item);
        else blocks.push({ type: 'list', ordered, items: [item] });
        return;
    }
    if (quote) {
        const runs = parseInline(quote[1], mentionIds);
        if (last?.type === 'quote') last.lines.push(runs);
        else blocks.push({ type: 'quote', lines: [runs] });
        return;
    }
    blocks.push(line.trim() ? { type: 'line', runs: parseInline(line, mentionIds) } : { type: 'gap' });
};

const parseInline = (line, mentionIds) => {
    const runs = [];
    let cursor = 0;
    for (const match of line.matchAll(CODE_SPAN)) {
        runs.push(...parseFormatted(line.slice(cursor, match.index), mentionIds));
        runs.push({ type: 'code', text: match[1] });
        cursor = match.index + match[0].length;
    }
    runs.push(...parseFormatted(line.slice(cursor), mentionIds));
    return runs;
};

const parseFormatted = (segment, mentionIds) => {
    if (!segment) return [];
    const atoms = [];
    const masked = segment.replace(ATOMS, (match, url, www, email, phone, mention, all) => {
        const lead = phone && /^\s/.test(phone) ? phone[0] : '';
        atoms.push(toAtom({ url, www, email, phone, mention, all }, mentionIds));
        return lead + String.fromCharCode(ATOM_BASE + atoms.length - 1);
    });
    return parseEmphasis(masked).flatMap(run => restoreAtoms(run, atoms));
};

const toAtom = ({ url, www, email, phone, mention, all }, mentionIds) => {
    if (url || www) {
        const link = (url || www).replace(TRAILING_PUNCTUATION, '');
        const tail = (url || www).slice(link.length);
        return [
            { type: 'link', href: url ? link : `https://${link}`, text: link },
            ...(tail ? [{ type: 'text', text: tail }] : []),
        ];
    }
    if (email) return [{ type: 'link', href: `mailto:${email}`, text: email }];
    if (phone) {
        const number = phone.trim();
        return [{ type: 'link', href: `tel:${number.replace(/[\s.-]/g, '')}`, text: number }];
    }
    if (mention) {
        const userId = mentionIds.shift();
        return [userId ? { type: 'mention', userId } : { type: 'text', text: '@' }];
    }
    return [{ type: all ? 'mentionAll' : 'text', text: all || '' }];
};

const parseEmphasis = text => {
    const found = EMPHASIS_RULES.map(rule => ({ rule, match: text.match(rule.pattern) }))
        .filter(({ match }) => match)
        .sort((a, b) => a.match.index - b.match.index)[0];
    if (!found) return text ? [{ type: 'text', text }] : [];
    const { rule, match } = found;
    return [
        ...(match.index ? [{ type: 'text', text: text.slice(0, match.index) }] : []),
        { type: rule.type, runs: parseEmphasis(match[1]) },
        ...parseEmphasis(text.slice(match.index + match[0].length)),
    ];
};

const restoreAtoms = (run, atoms) => {
    if (run.runs) return [{ ...run, runs: run.runs.flatMap(child => restoreAtoms(child, atoms)) }];
    if (run.type !== 'text') return [run];
    const restored = [];
    let cursor = 0;
    for (const match of run.text.matchAll(ATOM_MARK)) {
        const atom = atoms[match[0].charCodeAt(0) - ATOM_BASE];
        if (!atom) continue;
        if (match.index > cursor) restored.push({ type: 'text', text: run.text.slice(cursor, match.index) });
        restored.push(...atom);
        cursor = match.index + 1;
    }
    if (cursor < run.text.length) restored.push({ type: 'text', text: run.text.slice(cursor) });
    return restored;
};

// Older web builds sent links as <a> markup and spaces as &nbsp;.
const cleanLegacyMarkup = (text = '') =>
    String(text || '')
        .replace(/&nbsp;/g, ' ')
        .replace(/<a\s[^>]*href=["']([^"']+)["'][^>]*>[^<]*<\/a>(\1)?/gi, '$1');
